import React, { useEffect, useMemo, useState } from "react";
import { Croissant, Coffee, CalendarDays, Flame, Cookie, CupSoda, GlassWater, Beef, Leaf, Wheat, Search, Star, Share2, UtensilsCrossed } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { toast } from "sonner";

const ICONS = { Croissant, Coffee, CalendarDays, Flame, Cookie, CupSoda, GlassWater, Beef, UtensilsCrossed };
const A_LABELS = {};
const DAYS = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag"];
const todayIdx = () => { const d = new Date().getDay(); return d === 0 || d === 6 ? 0 : Math.min(d - 1, 4); };

function ItemCard({ it }) {
  const share = () => {
    const txt = `HELIUM: ${it.name} (${it.price?.toFixed(2)} €)`;
    if (navigator.share) navigator.share({ title: "HELIUM Speisekarte", text: txt }).catch(() => {});
    else { navigator.clipboard.writeText(txt); toast.success("Link kopiert"); }
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all overflow-hidden group flex flex-col" data-testid="menu-item-card">
      {it.image ? (
        <div className="h-40 overflow-hidden"><img src={it.image} alt={it.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
      ) : (
        <div className="h-40 bg-gradient-to-br from-[#FDF2F4] to-[#F9F8F6] flex items-center justify-center"><UtensilsCrossed className="w-10 h-10 text-[#E0243A]/40" /></div>
      )}
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-lg font-semibold leading-tight">{it.name}</h3>
          <div className="text-right whitespace-nowrap">
            <div className="font-bold text-[#E0243A]">{it.price?.toFixed(2)} €</div>
            {it.ma_price != null && <div className="text-xs font-semibold text-gray-500" data-testid="menu-item-ma-price">MA {it.ma_price.toFixed(2)} €</div>}
          </div>
        </div>
        {it.description && <p className="text-sm text-gray-600 mt-1">{it.description}</p>}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {it.dietary?.includes("vegetarisch") && <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200/60 px-2 py-0.5 rounded-full text-xs font-semibold"><Leaf className="w-3 h-3" />Vegetarisch</span>}
          {it.dietary?.includes("vegan") && <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 border border-green-200/60 px-2 py-0.5 rounded-full text-xs font-semibold"><Leaf className="w-3 h-3" />Vegan</span>}
          {it.allergen_codes?.length > 0 && it.allergen_codes.map((c) => <span key={c} className="inline-flex items-center bg-[#6F6F6F] text-white px-2 py-0.5 rounded-full text-xs font-semibold" data-testid="menu-allergen-chip">#{c}-{A_LABELS[c] || c}</span>)}
          {!it.allergen_codes?.length && it.allergens?.length > 0 && <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200/60 px-2 py-0.5 rounded-full text-xs font-semibold"><Wheat className="w-3 h-3" />{it.allergens.join(", ")}</span>}
        </div>
        <div className="mt-3 flex items-center justify-between pt-3 border-t border-gray-50">
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#C81E33] bg-[#FDF2F4] px-2 py-1 rounded-full"><Star className="w-3 h-3" />{it.points_required} Punkte</span>
          <button onClick={share} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#E0243A]"><Share2 className="w-4 h-4" /> Teilen</button>
        </div>
      </div>
    </div>
  );
}

export default function Speisekarte() {
  const [categories, setCategories] = useState([]);
  const [itemsByCat, setItemsByCat] = useState({});
  const [weekly, setWeekly] = useState({ days: {}, week_start: null });
  const [active, setActive] = useState("");
  const [day, setDay] = useState(DAYS[todayIdx()]);
  const [diet, setDiet] = useState("Alle");
  const [search, setSearch] = useState("");
  const [, setCatReady] = useState(false);

  useEffect(() => {
    api.get("/menu/categories").then(async (r) => {
      setCategories(r.data);
      if (r.data.length && !active) setActive(r.data[0].id);
    });
    api.get("/menu/items").then((r) => {
      const grouped = {};
      r.data.forEach((it) => { (grouped[it.category] = grouped[it.category] || []).push(it); });
      setItemsByCat(grouped);
    });
    api.get("/menu/weekly").then((r) => setWeekly(r.data));
    api.get("/allergens/catalog").then((r) => { r.data.forEach((a) => { A_LABELS[a.code] = a.label; }); setCatReady(true); }).catch(() => {});
  }, []);

  const applyDiet = (arr) => {
    if (diet === "Vegetarisch") return arr.filter((i) => i.dietary?.includes("vegetarisch"));
    if (diet === "Vegan") return arr.filter((i) => i.dietary?.includes("vegan"));
    return arr;
  };

  // combined searchable list across all categories + weekly
  const searchResults = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    const all = [];
    Object.entries(itemsByCat).forEach(([cat, arr]) => arr.forEach((it) => all.push({ ...it, _cat: cat })));
    Object.values(weekly.days || {}).forEach((arr) => (arr || []).forEach((it) => all.push({ ...it, _cat: "wochenmenue" })));
    return applyDiet(all.filter((it) =>
      (it.name || "").toLowerCase().includes(q) ||
      (it.description || "").toLowerCase().includes(q) ||
      (it.category || it._cat || "").toLowerCase().includes(q) ||
      (it._cat === "wochenmenue" && "wochenmenü kaffee frühstück grill süßigkeiten getränke".includes(q))
    ));
  }, [search, itemsByCat, weekly, diet]);

  const activeCat = categories.find((c) => c.id === active);
  const weeklyItems = applyDiet(weekly.days?.[day] || []);
  const permItems = applyDiet(itemsByCat[active] || []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <Seo title="Speisekarte – HELIUM Cafeteria" description="Die HELIUM Speisekarte: Frühstück, Kaffee, wöchentliches Wochenmenü, Grill & Fast Food, Süßigkeiten und Getränke – frisch, mit Allergenkennzeichnung." />
      <span className="text-xs font-semibold uppercase tracking-wider text-[#E0243A]">Speisekarte</span>
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-2 font-display">Alles frisch. Für jeden Geschmack.</h1>

      {/* Search */}
      <div className="mt-6 relative max-w-md">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Suchen (z. B. Kaffee, vegetarisch…)"
          data-testid="menu-search" className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#E0243A] outline-none" />
      </div>

      {/* Category tabs */}
      {!search.trim() && (
        <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="menu-category-tabs">
          {categories.map((c) => {
            const Icon = ICONS[c.icon] || UtensilsCrossed;
            const isWeekly = c.type === "weekly";
            return (
              <button key={c.id} onClick={() => setActive(c.id)} data-testid={`menu-cat-${c.id}`}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium whitespace-nowrap transition-all ${active === c.id ? "bg-[#E0243A] text-white shadow" : "bg-white border border-gray-200 text-gray-700 hover:border-[#E0243A]/40"}`}>
                <Icon className="w-4 h-4" /> {c.name}
                {isWeekly && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${active === c.id ? "bg-white/20" : "bg-[#FDF2F4] text-[#C81E33]"}`}>Diese Woche</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Diet filter */}
      <div className="mt-4 flex gap-2">
        {["Alle", "Vegetarisch", "Vegan"].map((f) => (
          <button key={f} onClick={() => setDiet(f)} className={`px-4 py-1.5 rounded-full text-sm font-medium ${diet === f ? "bg-[#FDF2F4] text-[#C81E33] border border-[#E0243A]/30" : "bg-white border border-gray-200 text-gray-600"}`}>{f}</button>
        ))}
      </div>

      {/* CONTENT */}
      {search.trim() ? (
        <div className="mt-8">
          <p className="text-sm text-gray-500 mb-4">{searchResults.length} Ergebnis(se) für „{search}"</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {searchResults.map((it, i) => <ItemCard key={i} it={it} />)}
            {!searchResults.length && <p className="text-gray-500 col-span-full">Keine Treffer.</p>}
          </div>
        </div>
      ) : activeCat?.type === "weekly" ? (
        <div className="mt-8">
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {DAYS.map((d) => (
              <button key={d} onClick={() => setDay(d)} data-testid={`day-tab-${d.toLowerCase()}`}
                className={`px-5 py-2.5 rounded-xl font-medium whitespace-nowrap ${day === d ? "bg-[#1F2937] text-white" : "bg-white border border-gray-200 text-gray-700"}`}>{d}</button>
            ))}
            <button onClick={() => setDay("__overview__")} data-testid="day-tab-overview"
              className={`px-5 py-2.5 rounded-xl font-medium whitespace-nowrap inline-flex items-center gap-2 ${day === "__overview__" ? "bg-[#E0243A] text-white" : "bg-white border border-[#E0243A]/40 text-[#E0243A]"}`}><CalendarDays className="w-4 h-4" /> Wochenübersicht</button>
          </div>
          {day === "__overview__" ? (
            <div className="mt-6 space-y-8" data-testid="week-overview">
              {DAYS.map((d) => {
                const its = applyDiet(weekly.days?.[d] || []);
                return (
                  <div key={d} data-testid={`week-overview-${d.toLowerCase()}`}>
                    <h3 className="text-lg font-bold text-[#1F2937] border-b border-gray-200 pb-2 mb-4">{d}</h3>
                    {its.length ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {its.map((it, i) => <ItemCard key={i} it={it} />)}
                      </div>
                    ) : <p className="text-gray-500 text-sm">Kein Gericht hinterlegt.</p>}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {weeklyItems.length ? weeklyItems.map((it, i) => <ItemCard key={i} it={it} />)
                : <p className="text-gray-500 col-span-full">Für {day} ist kein Gericht hinterlegt.</p>}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8">
          {activeCat?.subtitle && <p className="text-gray-500 mb-4 -mt-2">{activeCat.subtitle}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {permItems.length ? permItems.map((it) => <ItemCard key={it.id} it={it} />)
              : (
                <div className="col-span-full text-center py-14 bg-[#F9F8F6] rounded-2xl" data-testid="category-empty">
                  <UtensilsCrossed className="w-10 h-10 text-gray-300 mx-auto" />
                  <p className="mt-3 text-gray-600">In dieser Kategorie sind bald Artikel verfügbar.</p>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
